"use client"

import { cn } from "@/lib/utils"

import type React from "react"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { apiRequestWithAuth, getStoredToken } from "@/lib/api-config"
import { CircleNotch as Loader2 } from "@phosphor-icons/react"

interface AnnouncementData {
  context: string
  title: string
  md5: string
}

interface AnnouncementResponse {
  code: number
  msg: string
  data: AnnouncementData
}

export default function AdminSettingsPage() {
  const { toast } = useToast()
  const [currentTitle, setCurrentTitle] = useState("")
  const [currentContext, setCurrentContext] = useState("")
  const [newTitle, setNewTitle] = useState("")
  const [newContext, setNewContext] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchAnnouncement = async () => {
    setIsLoading(true)
    const token = getStoredToken()
    if (!token) {
      toast({
        title: "错误",
        description: "未找到认证令牌，请重新登录。",
        variant: "destructive",
      })
      setIsLoading(false)
      return
    }

    try {
      const response: AnnouncementResponse = await apiRequestWithAuth("/getAnnouncement", token)
      if (response.code === 200 && response.data) {
        setCurrentTitle(response.data.title)
        setCurrentContext(response.data.context)
        setNewTitle(response.data.title)
        setNewContext(response.data.context)
      } else {
        toast({
          title: "获取公告失败",
          description: response.msg || "未知错误。",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "获取公告失败",
        description: "无法连接到服务器或网络错误。",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAnnouncement()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const token = getStoredToken()
    if (!token) {
      toast({
        title: "错误",
        description: "未找到认证令牌，请重新登录。",
        variant: "destructive",
      })
      setIsSubmitting(false)
      return
    }

    try {
      const response = await apiRequestWithAuth("/createAnnouncement", token, {
        method: "POST",
        body: JSON.stringify({ title: newTitle, context: newContext }),
      })

      if (response.code === 200) {
        toast({
          title: "公告更新成功",
          description: "公告已成功发布。",
        })
        await fetchAnnouncement()
      } else {
        toast({
          title: "公告更新失败",
          description: response.msg || "未知错误。",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "公告更新失败",
        description: "无法连接到服务器或网络错误。",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
      <div className="flex flex-col gap-6 p-4 md:p-6">
        <div className="page-title-box">
          <div className="page-title">其他设置</div>
        </div>{/* 公告管理卡片 */}
        <Card style={{ border: "1.5px solid var(--grape-400)", background: "radial-gradient(circle, rgba(183,125,238,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(220,180,255,0.12) 1px, transparent 1px), var(--grape-50)", color: "var(--grape-600)" }} className="w-full max-w-4xl mx-auto bg-white  border-grape-400 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-grape-600">公告管理</CardTitle>
            <CardDescription className="text-gray-500 text-soil-600">
              在此管理和发布系统公告。新公告将覆盖现有公告。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-gray-600 text-soil-600">加载中...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-grape-600">当前公告:</h3>
                <Card className="p-4 border border-grape-400 ">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    <span className="font-bold">标题:</span> {currentTitle || "无"}
                  </p>
                  <p className="text-sm text-gray-600 text-soil-600 mt-2">
                    <span className="font-bold">内容:</span> {currentContext || "无"}
                  </p>
                </Card>

                <Separator className="my-6 " />

                <h3 className="text-lg font-semibold text-grape-600">发布新公告:</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newTitle" className="text-grape-600">
                      公告标题
                    </Label>
                    <Input
                      id="newTitle"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="请输入公告标题"
                      required
                      className=" text-grape-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newContext" className="text-grape-600">
                      公告内容
                    </Label>
                    <Textarea
                      id="newContext"
                      value={newContext}
                      onChange={(e) => setNewContext(e.target.value)}
                      placeholder="请输入公告内容"
                      rows={5}
                      required
                      className=" text-grape-600"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        发布中...
                      </>
                    ) : (
                      "发布公告"
                    )}
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  )
}

function Separator({ className }: { className?: string }) {
  return <div className={cn("h-px bg-gray-200", className)} />
}
